import { useQuery, useQueryClient } from '@tanstack/react-query';

import { API } from '@/shared/api';
import { getGA4Context } from '@/shared/lib/attribution';
import { User } from '@/shared/types/User';

import { GA4_CONTEXT_QUERY_KEY } from '../lib/constants';
import { USER_QUERY_KEYS } from '../lib/userQueryKeys';

export const useSyncGA4ClientId = (user?: User) => {
  const queryClient = useQueryClient();
  const userId = user?.id;

  useQuery({
    enabled: Boolean(userId),
    queryFn: async () => {
      if (!userId) return null;

      const context = getGA4Context();
      if (!context) return null;

      const current = queryClient.getQueryData<User>(USER_QUERY_KEYS.userData);
      if (
        !current ||
        current.id !== userId ||
        (current.ga4_client_id === context.clientId && current.ga4_session_id === context.sessionId)
      ) {
        return null;
      }

      const response = await API.saveGA4ClientId(context.clientId, context.sessionId);
      queryClient.setQueryData<User>(USER_QUERY_KEYS.userData, (cachedUser) =>
        cachedUser?.id === userId ? { ...cachedUser, ...response } : cachedUser,
      );

      return response;
    },
    queryKey: [...GA4_CONTEXT_QUERY_KEY, userId],
    refetchInterval: 60_000,
    refetchOnWindowFocus: 'always',
    retry: false,
  });
};
